import { GameWeek, Season, TeamNamesByID } from '@shared/constants';
import { getPrefixForOrderedUID, getSeasonIdFromGameID, getTeamScoringByQtrString } from '@shared/helpers';
import {
  ByeWeekID,
  DecomposedUID,
  DeconstructedByeWeekID,
  DeconstructedSeasonTeamAbrvID,
  DeconstructedSeasonWeekID,
  DeconstructedSeasonWeekTeamAbrvID,
  DeconstructedTeamAbrvID,
  GameID,
  GameWithScoreByQtrID,
  GameWithScoreID,
  ITeamLineScore,
  SeasonID,
  SeasonTeamAbrvID,
  SeasonWeekID,
  SeasonWeekTeamAbrvID,
  StringOrNumber,
  TeamAbrvID,
  TeamAbrvType,
  TeamID,
  UID,
  WeekID,
} from '@shared/types';
import { Cls, Emojis } from 'app/constants';

// #region UID ****************************************************************

/**
 * `uID` list with ordered `weekID`, `teamID`.
 *
 * @param {WeekID} weekID - e.g. "Week1"
 * @param {TeamID} teamID - e.g. "5"
 * @param {GameID} gameID - e.g. "20250901_SF@ARI"
 *
 * @return `YYYY-DD-DD-weekID-teamID-TeamAbrv-gameID` were the first DD is the weekID with leading zero for weeks 1-9,
 * and the second DD is the teamID with leading zero for teams 1-9.
 */

export const toUID = (weekID: WeekID, teamID: TeamID, gameID: GameID, yearID: SeasonID = Season.Season2024): UID => {
  let seasonID = yearID;
  if (gameID !== 'BYE') {
    seasonID = getSeasonIdFromGameID(gameID);
  }
  const orderedPair = getPrefixForOrderedUID(weekID, teamID);
  return `${seasonID}-${orderedPair}-${toWeekID(weekID)}-${teamID}-${TeamNamesByID[parseInt(teamID)]}-${gameID}`;
};

export const breakUID = (orderedUID: UID): DecomposedUID => {
  let uID = orderedUID;
  // If not in the new format (YYYY-...), treat as old UID
  if (!/^\d{4}-/.test(orderedUID)) {
    // console.log(`${Emojis.warning} %c Old UID %c still present %c ${uID} %c`, Cls.Cri, '', Cls.Val, '');
    const splitted = orderedUID.split('-');
    const gameID = splitted[splitted.length - 1];
    const YYYY = getSeasonIdFromGameID(gameID);
    uID = `${YYYY}-${orderedUID}`;
  }
  // regex to check for string 2024-09-15-Week9-15-JAX-20241103_JAX@PHI, 2024-09-27-Week9-27-PHI-20241103_JAX@PHI, etc
  const regex = /(\d{4})-(\d{2})-(\d{2})-Week(\d{1,2})-(\d{1,2})-(\w{2,3})-(\d{8}_\w{2,3}@\w{2,3})/;
  // regex to check for string 2024-09-15-Week9-15-JAX-BYE
  const regexBye = /(\d{4})-(\d{2})-(\d{2})-Week(\d{1,2})-(\d{1,2})-(\w{2,3})-BYE/;

  // combine the two previous regexes
  const regexCombined = new RegExp(`(${regex.source})|(${regexBye.source})`);

  // test for the regex
  if (!regexCombined.test(uID)) {
    throw new Error(`Invalid orderedUID format <${orderedUID}>`);
  }

  const [yearID, weekNum, teamNum, weekID, teamID, teamAbrv, gameID] = uID.split('-');
  const seasonWeekID = toSeasonWeekID(yearID as SeasonID, toWeekID(weekNum));
  return {
    yearID,
    weekNum: parseInt(weekNum, 10),
    teamNum: parseInt(teamNum, 10),
    weekID: toWeekID(weekID),
    teamID,
    teamAbrv,
    gameID,
    seasonWeekID,
  };
};
// #endregion

// #region WeekID *************************************************************

/** Converts a week value (number or string) to a `GameWeek` enum.
 * @param {number | string} weekValue - The week value to convert. Can be a number (1-18) or a string in the format "WeekX".
 * @return {GameWeek} The corresponding `GameWeek` enum value.
 * @throws {Error} If the input is invalid or out of range.
 * @example
 * toWeekID(1) // returns GameWeek.Week1
 * toWeekID("Week5") // returns GameWeek.Week5
 * toWeekID("  week10 ") // returns GameWeek.Week10
 * toWeekID("") // returns GameWeek.Week1
 * toWeekID(20) // throws Error
 * toWeekID("WeekX") // throws Error
 * toWeekID("InvalidString") // throws Error
 * toWeekID(0) // throws Error
 * toWeekID(-3) // throws Error
 * toWeekID("Week") // throws Error
 */
export const toWeekID = (weekValue: number | string): GameWeek => {
  let weekNum = 0;
  if (typeof weekValue === 'string') {
    if (weekValue.trim() === '' || weekValue.trim().toLowerCase() === 'all') {
      return GameWeek.Week1;
    }
    const weekString = weekValue.trim().toLowerCase().replace(' ', '').replace('week', '') ?? '1';
    weekNum = parseInt(weekString, 10);
  } else {
    weekNum = weekValue;
  }

  const isWeekValueNumber = typeof weekNum === 'number' || !Number.isNaN(weekNum);
  if (isWeekValueNumber) {
    if (weekNum >= 1 && weekNum <= 18) {
      return GameWeek[`Week${weekNum}` as keyof typeof GameWeek];
    }
  }

  const errorMsg = `From toWeekID - Invalid week value: <${weekValue}>`;
  const errorReason = `Must be a number, or a string in the format ${'"WeekX"'} where X is between 1 and 18.`;
  const fullErrorMsg = `${errorMsg} ${errorReason}`;
  console.warn(`${Emojis.warning} toWeekID | ${fullErrorMsg}`);

  // return GameWeek.Week1;
  throw new Error(fullErrorMsg);
};

/**
 * Converts a weekID string to its numerical representation.
 * Handles various formats including "Week1", "Week17", and "all".
 *
 * @param {WeekID} weekID - The week identifier (e.g., "Week1", "Week17")
 * @return {number} The numerical value of the week (e.g., 1, 17)
 * @example
 * toNumber("Week1") // returns 1
 * toNumber("Week17") // returns 17
 */
export const toNumber = (weekID: WeekID): number => {
  if (!weekID) {
    const msg = `${Emojis.warning} %c toNumber %c %c weekID %c is %c undefined %c defaulting to 1`;
    console.warn(msg, Cls.Loc, '', Cls.Info, '', Cls.Val, '');
    return 1; // Default to 1 if input is invalid
  }
  if (typeof weekID === 'number' && weekID >= 1 && weekID <= 18) {
    return weekID;
  } else if (weekID === 'all') {
    return 1;
  } else if (typeof weekID === 'string') {
    return parseInt(weekID.toLowerCase().replace('week', '').trim(), 10);
  }
  const msg = `${Emojis.warning} %c toNumber %c Invalid weekID %c ${weekID} %c  defaulting to 1`;
  console.warn(msg, Cls.Cri, '', Cls.Val, '');
  return 1; // Default to 1 if input is invalid
};
// #endregion

// #region ByeWeekID **********************************************************

/**
 * Returns an unique id that contains SeasonID with zero padded WeekID and TeamID and an abbreviated team name.
 *
 * @param {WeekID} weekID
 * @param {TeamID} teamID
 * @param {SeasonID} seasonID
 *
 * @return {ByeWeekID} a formatted ByeWeekID string
 *
 * @example 2025-03-01-ARI
 */
export const toByeWeekID = (weekID: WeekID, teamID: string, seasonID: SeasonID): ByeWeekID => {
  const weekNum = `${toNumber(weekID)}`.padStart(2, '0');
  const teamNum = `${teamID}`.padStart(2, '0');
  const teamAbrv = TeamNamesByID[parseInt(teamID, 10)] || 'UNK';
  return `${seasonID}-${weekNum}-${teamNum}-${teamAbrv}`;
};

/**
 * Breaks down a ByeWeekID into its constituent parts.
 *
 * @param {ByeWeekID} byeWeekID - the ByeWeekID to break down
 *
 * @return {DeconstructedByeWeekID} - an object containing seasonID, weekID, teamID, and teamAbrv
 */
export const breakByeWeekID = (byeWeekID: ByeWeekID): DeconstructedByeWeekID => {
  const [season, week, team, abrv] = byeWeekID.split('-');
  const seasonID = season as SeasonID;
  const weekID = toWeekID(week);
  const seasonWeekID = toSeasonWeekID(seasonID, weekID);
  const teamID = toTeamID(team);
  const teamAbrv = abrv as TeamAbrvType;
  const teamAbrvID = toTeamAbrvID(teamID);
  const seasonTeamAbrvID = toSeasonTeamAbrvID(seasonID, teamID);
  return { seasonID, weekID, seasonWeekID, teamID, teamAbrv, teamAbrvID, seasonTeamAbrvID };
};
// #endregion

// #region TeamID *************************************************************

/**
 * Converts a team number string to a TeamID.
 *
 * @param {string} teamNumAsStr - the team number as a string
 *
 * @return {TeamID} - the TeamID as a string
 */
export const toTeamID = (teamNumAsStr: string): TeamID => parseInt(teamNumAsStr, 10).toString();

// #endregion

// #region TeamAbrvID *********************************************************

/**
 * Returns an unique id that contains zero padded teamID and an abbreviated team name.
 *
 * @param {TeamID} teamID
 *
 * @return {TeamAbrvID} a zero padded teamID + team abbreviated string
 *
 * @example 01-ARI, 02-ATL, ...
 */
export const toTeamAbrvID = (teamID: TeamID): TeamAbrvID => {
  return `${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a team abbreviated ID into its constituent parts.
 *
 * @param {TeamAbrvID} teamAbrvID the team abbreviated ID to break down
 *
 * @return {DeconstructedTeamAbrvID} an object containing teamID and teamAbrv
 */
export const breakTeamAbrvID = (teamAbrvID: TeamAbrvID): DeconstructedTeamAbrvID => {
  const [teamIDWithZeroPadding, teamAbrv] = teamAbrvID.split('-');
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  return {
    teamID,
    teamAbrv: teamAbrv as TeamAbrvType,
  };
};
// #endregion

// #region SeasonWeekID *******************************************************

/**
 * Returns an unique id that contains SeasonID and a zero padded WeekID.
 *
 * @param {SeasonID} year - the season year
 * @param {WeekID} week - the week ID
 *
 * @return {SeasonWeekID} - a formatted SeasonWeekID string
 */
export const toSeasonWeekID = (year: SeasonID, week: WeekID): SeasonWeekID => {
  const weekNum = toNumber(week);
  return `${year}-${String(weekNum).padStart(2, '0')}` as SeasonWeekID;
};

/**
 * Breaks down a SeasonWeekID into its constituent parts.
 *
 * @param {SeasonWeekID} seasonWeekID - the SeasonWeekID to break down
 *
 * @return {DeconstructedSeasonWeekID} - an object containing yearID and weekID
 */
export const breakSeasonWeekID = (seasonWeekID: SeasonWeekID): DeconstructedSeasonWeekID => {
  const [year, week] = seasonWeekID.split('-');
  return { yearID: year, weekID: toWeekID(week), zeroPaddedWeekID: week, numericWeekID: parseInt(week, 10) };
};
// #endregion

// #region SeasonTeamAbrvID ***************************************************

/**
 * Returns an unique id that contains SeasonID with zero padded teamID and an abbreviated team name.
 *
 * @param {SeasonID} seasonID
 * @param {TeamID} teamID
 *
 * @return {SeasonTeamAbrvID} a formatted season-teamID-abbreviated team name ID
 */
export const toSeasonTeamAbrvID = (seasonID: SeasonID, teamID: TeamID): SeasonTeamAbrvID => {
  return `${seasonID}-${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a season-team abbreviated ID into its constituent parts.
 *
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID the season-team abbreviated ID to break down
 *
 * @return {DeconstructedSeasonTeamAbrvID} an object containing seasonID, teamID, teamAbrvID, and teamAbrv
 */
export const breakSeasonTeamAbrvID = (seasonTeamAbrvID: SeasonTeamAbrvID): DeconstructedSeasonTeamAbrvID => {
  const [seasonID, teamIDWithZeroPadding, teamAbrv] = seasonTeamAbrvID.split('-');
  const teamAbrvID = toTeamAbrvID(teamIDWithZeroPadding);
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  return {
    seasonID,
    teamID,
    teamAbrvID,
    teamAbrv: teamAbrv as TeamAbrvType,
  };
};

/**
 * Returns an array of all SeasonTeamAbrvIDs for the given season.
 *
 * @param {SeasonID} seasonID - the season year
 *
 * @return {SeasonTeamAbrvID[]} - array of all SeasonTeamAbrvIDs for the given season
 */
export const getAllTeamsSeasonTeamAbrvIDs = (seasonID: SeasonID): SeasonTeamAbrvID[] => {
  return Object.entries(TeamAbrvToTeamID).map(([, teamID]) => {
    return toSeasonTeamAbrvID(seasonID, teamID);
  });
};
// #endregion

// #region SeasonWeekTeamAbrvID ***********************************************

/**
 * Returns an unique id that contains SeasonID with zero padded WeekID, TeamID and an abbreviated team name.
 *
 * @param {SeasonID} seasonID
 * @param {WeekID} weekID
 * @param {TeamID} teamID
 *
 * @return {SeasonWeekTeamAbrvID} a formatted season-weekID-teamID-abbreviated team name ID
 */
export const toSeasonWeekTeamAbrvID = (seasonID: SeasonID, weekID: WeekID, teamID: TeamID): SeasonWeekTeamAbrvID => {
  return `${seasonID}-${String(toNumber(weekID)).padStart(2, '0')}-${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a season-team abbreviated ID into its constituent parts.
 *
 * @param {SeasonWeekTeamAbrvID} seasonTeamAbrvID the season-team abbreviated ID to break down
 *
 * @return {DeconstructedSeasonWeekTeamAbrvID} an object containing seasonID, weekID, teamID, teamAbrvID, and teamAbrv
 */
export const breakSeasonWeekTeamAbrvID = (
  seasonWeekTeamAbrvID: SeasonWeekTeamAbrvID,
): DeconstructedSeasonWeekTeamAbrvID => {
  const [seasonID, zeroPaddedWeekID, teamIDWithZeroPadding, teamAbrv] = seasonWeekTeamAbrvID.split('-');
  const teamAbrvID = toTeamAbrvID(teamIDWithZeroPadding);
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  const weekID = toWeekID(zeroPaddedWeekID);
  const numericWeekID = parseInt(zeroPaddedWeekID, 10);
  const seasonTeamAbrvID: SeasonTeamAbrvID = toSeasonTeamAbrvID(seasonID, teamID);
  return {
    yearID: seasonID,
    seasonID,
    seasonWeekID: toSeasonWeekID(seasonID, weekID),
    weekID,
    numericWeekID,
    zeroPaddedWeekID,
    teamID,
    teamAbrvID,
    teamAbrv: teamAbrv as TeamAbrvType,
    seasonTeamAbrvID,
  };
};

// #endregion

// #region GAME WITH SCORE IDs ************************************************
export const toGameWithScoreID = (
  gameID: GameID,
  awayScore: StringOrNumber = 0,
  homeScore: StringOrNumber = 0,
): GameWithScoreID => {
  return `${gameID}=${awayScore}:${homeScore}`;
};

// /**
//  * "20250904_DAL@PHI"
//  * @param {GameID} gameID
//  *
//  * @return {DeconstructedGameID}
//  */
// export const breakGameID = (gameID: GameID): DeconstructedGameID => {
//   const [, teamsPart] = gameID.split('_');
//   const [awayTeamAbrv, homeTeamAbrv] = teamsPart.split('@');
//   if (!awayTeamAbrv || !homeTeamAbrv) {
//     throw new Error(`Invalid GameID format <${gameID}>`);
//   }

//   const homeTeamID = TeamAbrvToTeamID[homeTeamAbrv as TeamAbrvType] as TeamID;
//   const awayTeamID = TeamAbrvToTeamID[awayTeamAbrv as TeamAbrvType] as TeamID;

//   return {
//     gameID: gameID as GameID,
//     homeTeamID: homeTeamID,
//     awayTeamID: awayTeamID,
//     homeTeamAbrv: homeTeamAbrv as TeamAbrvType,
//     awayTeamAbrv: awayTeamAbrv as TeamAbrvType,
//     homeTeamAbrvID: toTeamAbrvID(homeTeamID),
//     awayTeamAbrvID: toTeamAbrvID(awayTeamID),
//   };
// };

// /**
//  * 20250904_DAL@PHI=20:24
//  * @param gameWithScoreID
//  * @returns
//  */
// export const breakGameWithScoreID = (gameWithScoreID: GameWithScoreID): DeconstructedGameWithScoreID => {
//   const [gameID, scorePart] = gameWithScoreID.split('=');
//   const [awayScoreStr, homeScoreStr] = scorePart.split(':');
//   const [, teamsPart] = gameID.split('_');
//   const [awayTeamAbrv, homeTeamAbrv] = teamsPart.split('@');
//   const homeTeamID = TeamAbrvToTeamID[homeTeamAbrv as TeamAbrvType] as TeamID;
//   const awayTeamID = TeamAbrvToTeamID[awayTeamAbrv as TeamAbrvType] as TeamID;

//   return {
//     gameID: gameID as GameID,
//     homeTeamID: homeTeamID,
//     awayTeamID: awayTeamID,
//     homeTeamAbrv: homeTeamAbrv as TeamAbrvType,
//     awayTeamAbrv: awayTeamAbrv as TeamAbrvType,
//     homeTeamAbrvID: toTeamAbrvID(homeTeamID),
//     awayTeamAbrvID: toTeamAbrvID(awayTeamID),
//     homeTeamScore: parseInt(homeScoreStr),
//     awayTeamScore: parseInt(awayScoreStr),
//   };
// };

// #endregion

// #region
export const toGameWithScoreByQtrID = (
  gameID: GameID,
  awayLineScore: ITeamLineScore,
  homeLineScore: ITeamLineScore,
): GameWithScoreByQtrID => {
  return `${gameID}=${getTeamScoringByQtrString(awayLineScore)}:${getTeamScoringByQtrString(homeLineScore)}`;
};

// const generateTeamLineScore = (teamScore: string, teamID: TeamID, teamAbv: TeamAbrvType): ITeamLineScore => {
//   const scoreList = teamScore.split(',');
//   const totalPts = scoreList.pop() || '0';
//   const OT = scoreList.length === 5 ? scoreList.pop() : undefined;
//   const [Q1 = '0', Q2 = '0', Q3 = '0', Q4 = '0'] = scoreList;

//   const result: ITeamLineScore = {
//     Q1,
//     Q2,
//     Q3,
//     Q4,
//     OT,
//     totalPts,
//     teamID,
//     teamAbv,
//     currentlyInPossession: 'false',
//   };

//   return result;
// };

/** TODO: Create DeconstructedGameWithScoreByQtrID of shape
 *
 * ```tsx
 * export type DeconstructedGameWithScoreByQtrID = {
 *     homeTeamLineScore: ITeamLineScore;
 *     awayTeamLineScore: ITeamLineScore;
 * } & DeconstructedGameWithScoreID;
 * ```
 */
// export const breakGameWithScoreByQtrID = (
//   gameWithScoreByQtrID: GameWithScoreByQtrID,
// ): DeconstructedGameWithScoreByQtrID => {
//   const [gameID, scorePart] = gameWithScoreByQtrID.split('=');
//   const [awayScoreStr, homeScoreStr] = scorePart.split(':');
//   const awayScoreStrList = awayScoreStr.split(',');
//   const homeScoreStrList = homeScoreStr.split(',');
//   const awayTotalScore = awayScoreStrList[awayScoreStrList.length - 1];
//   const homeTeamScore = homeScoreStrList[homeScoreStrList.length - 1];
//   const result = breakGameWithScoreID(`${gameID}=${awayTotalScore}:${homeTeamScore}`);
//   const homeTeamLineScore: ITeamLineScore = generateTeamLineScore(homeScoreStr, result.homeTeamID, result.homeTeamAbrv);
//   const awayTeamLineScore: ITeamLineScore = generateTeamLineScore(awayScoreStr, result.awayTeamID, result.awayTeamAbrv);
//   const seasonWeekID = getSeasonWeekIDByGameID(gameID as GameID); // WeekID is a placeholder
//   return {
//     ...result,
//     homeTeamLineScore,
//     awayTeamLineScore,
//     seasonWeekID,
//   };
// };
// #endregion

// #region TEAM ABRV <-> TEAM ID Enums ****************************************

export enum TeamAbrvToTeamID {
  ARI = '1',
  ATL = '2',
  BAL = '3',
  BUF = '4',
  CAR = '5',
  CHI = '6',
  CIN = '7',
  CLE = '8',
  DAL = '9',
  DEN = '10',
  DET = '11',
  GB = '12',
  HOU = '13',
  IND = '14',
  JAX = '15',
  KC = '16',
  LV = '17',
  LAC = '18',
  LAR = '19',
  MIA = '20',
  MIN = '21',
  NE = '22',
  NO = '23',
  NYG = '24',
  NYJ = '25',
  PIT = '26',
  PHI = '27',
  SF = '28',
  SEA = '29',
  TB = '30',
  TEN = '31',
  WSH = '32',
}

export enum TeamIDToTeamAbrv {
  teamID_01 = 'ARI',
  teamID_02 = 'ATL',
  teamID_03 = 'BAL',
  teamID_04 = 'BUF',
  teamID_05 = 'CAR',
  teamID_06 = 'CHI',
  teamID_07 = 'CIN',
  teamID_08 = 'CLE',
  teamID_09 = 'DAL',
  teamID_10 = 'DEN',
  teamID_11 = 'DET',
  teamID_12 = 'GB ',
  teamID_13 = 'HOU',
  teamID_14 = 'IND',
  teamID_15 = 'JAX',
  teamID_16 = 'KC ',
  teamID_17 = 'LV ',
  teamID_18 = 'LAC',
  teamID_19 = 'LAR',
  teamID_20 = 'MIA',
  teamID_21 = 'MIN',
  teamID_22 = 'NE ',
  teamID_23 = 'NO ',
  teamID_24 = 'NYG',
  teamID_25 = 'NYJ',
  teamID_26 = 'PIT',
  teamID_27 = 'PHI',
  teamID_28 = 'SF ',
  teamID_29 = 'SEA',
  teamID_30 = 'TB ',
  teamID_31 = 'TEN',
  teamID_32 = 'WHH',
}

export const TeamNameMap = {
  [TeamAbrvToTeamID.ARI]: 'Cardinals',
  [TeamAbrvToTeamID.ATL]: 'Falcons',
  [TeamAbrvToTeamID.BAL]: 'Ravens',
  [TeamAbrvToTeamID.BUF]: 'Bills',
  [TeamAbrvToTeamID.CAR]: 'Panthers',
  [TeamAbrvToTeamID.CHI]: 'Bears',
  [TeamAbrvToTeamID.CIN]: 'Bengals',
  [TeamAbrvToTeamID.CLE]: 'Browns',
  [TeamAbrvToTeamID.DAL]: 'Cowboys',
  [TeamAbrvToTeamID.DEN]: 'Broncos',
  [TeamAbrvToTeamID.DET]: 'Lions',
  [TeamAbrvToTeamID.GB]: 'Packers',
  [TeamAbrvToTeamID.HOU]: 'Texans',
  [TeamAbrvToTeamID.IND]: 'Colts',
  [TeamAbrvToTeamID.JAX]: 'Jaguars',
  [TeamAbrvToTeamID.KC]: 'Chiefs',
  [TeamAbrvToTeamID.LAC]: 'Chargers',
  [TeamAbrvToTeamID.LAR]: 'Rams',
  [TeamAbrvToTeamID.LV]: 'Raiders',
  [TeamAbrvToTeamID.MIA]: 'Dolphins',
  [TeamAbrvToTeamID.MIN]: 'Vikings',
  [TeamAbrvToTeamID.NE]: 'Patriots',
  [TeamAbrvToTeamID.NO]: 'Saints',
  [TeamAbrvToTeamID.NYG]: 'Giants',
  [TeamAbrvToTeamID.NYJ]: 'Jets',
  [TeamAbrvToTeamID.PHI]: 'Eagles',
  [TeamAbrvToTeamID.PIT]: 'Steelers',
  [TeamAbrvToTeamID.SEA]: 'Seahawks',
  [TeamAbrvToTeamID.SF]: '49ers',
  [TeamAbrvToTeamID.TB]: 'Buccaneers',
  [TeamAbrvToTeamID.TEN]: 'Titans',
  [TeamAbrvToTeamID.WSH]: 'Commanders',
} as const;
// #endregion
